import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import Vue from 'vue';

import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

import { MODULE_NAME } from './const';
import {
	isTemp,
	isWidget,
	isEndRunWidget,
	isMiniApp,
	isBranches,
	isSearchOrCreate,
	isSendNotification,
	editNodeAction,
	previousStepsDataByNodeLocation,
} from './getters';
import {
	UPDATE_EDIT_NODE,
	UPDATE_EDIT_NODE_CREDENTIALS,
	SET_SIDE_BAR_STATE,
	SET_EDIT_NODE,
	SET_NEED_EXTRA_FIELDS,
	SET_LOADING_EXTRA_FIELDS,
	SET_NODE_PARAMS,
	SET_GET_EXTRA_FIELDS_PROMISE,
	SET_NODE_PARAMS_ID,
	IS_FLOW_TREE_DRAGGING,
	SET_FORCE_LOAD_DYNAMIC_FIELDS,
	SET_IS_NODE_DATA_LOADING,
	SET_TESTING_PROFILE,
	SYNC_TEMP_EDIT_NODE_COPY,
	RESET_TEMP_EDIT_NODE_COPY,
	ADD_AFTER_UPDATE_EDIT_NODE_HANDLER,
	RESET_AFTER_UPDATE_EDIT_NODE_HANDLERS,
	ADD_ATTRIBUTE_TO_UPDATE,
	RESET_ATTRIBUTES_TO_UPDATE,
	ADD_AUTOFILL,
	REMOVE_AUTOFILL,
	SET_IS_AUTOFILL_LOADING,
	SET_AUTOFILL_ERROR,
	RESET_AUTOFILL,
} from './mutations';
import state from './state';
import { createCancellablePromise } from '../../utils';
import StoreModuleService from '../StoreModuleService';
import { getAction, getActionParams } from '../actions/getters';
import { getActionsOfApp, getServiceData } from '../apps/actions';
import { getService, isActionSearchOrCreateOfService } from '../apps/getters';
import { setIsEditorAccordionMenuOpen } from '../blockEditor/actions';
import { getUserCredentials } from '../credentials/actions';
import { getAccountConnection } from '../credentials/getters';
import { isConfigurationsOn, isAiAutofillEnabled } from '../featureFlags/getters';
import { setLeftSideDrawerState } from '../leftSideDrawer/actions';
import { setLoading } from '../main/actions';
import {
	getNodeData,
	toggleNodeType as nodesToggleNodeType,
	updateNode,
	skipTest as nodeSkipTest,
	testStep as nodeTestStep,
	resetWidgetFieldTestStatus as resetNodeWidgetFieldTestStatus,
} from '../nodes/actions';
import { getNode, getNodeByUuidForWorkflowVersion } from '../nodes/getters';
import { reloadSelectedWorkflow } from '../selectedWorkflow/actions';
import { editedWorkflowVersion, selectedWorkflowId } from '../selectedWorkflow/getters';
import { createWorkflowPusherConnection, removeWorkflowPusherConnection } from '../subflows/actions';
import { removeTempBlock } from '../treeBlocks/actions';
import { initWidgetFields } from '../widgetEditor/actions';
import { getConfigurationFieldResponse } from '../workflowConfigurations/getters';
import { addNodeToWorkflowNodes } from '../workflows/actions';
import api, { notifications as notificationsApi } from '@/api';
import { INTERFACE_QUERY_PARAM } from '@/components/FlowTree/composables';
import WorkflowVersionService from '@/components/WorkflowVersionHistory/workflow-version-service';
import router from '@/router';
import { BUILDER_VERSION_HISTORY_ROUTE_NAME, BUILDER_TASK_LOG_INNER_PAGE, BUILDER_DEBUG_MODE_ROUTE_NAME } from '@/router/router.const';
import { BLOCK_EDITOR_ROUTE_NAME } from '@/router/router.const.js';
import { useIsTaskLogOpen } from '@/store/modules/analytics/getters.js';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { getWidget } from '@/store/modules/widgets/getters';
import { isVersionHistoryOpen } from '@/store/modules/workflows/modules/versions/getters.js';
import { suggestAutofill, shouldAutofillParam } from '@/utils/autofill-service';
import ErrorHandlingService from '@/utils/error-handling-service';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import { NODE_ERROR_CODES } from '@/utils/workflow-node.const.js';

const buildSelectedNodeActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const toggleNodeType = buildSelectedNodeActionObject('toggleNodeType');
export const updateEditNode = buildSelectedNodeActionObject('updateEditNode');
export const updateEditNodeTestResultData = buildSelectedNodeActionObject('updateEditNodeTestResultData');
export const updateEditNodeTestResultOutputLabels = buildSelectedNodeActionObject('updateEditNodeTestResultOutputLabels');
export const updateEditNodeTestResultStatus = buildSelectedNodeActionObject('updateEditNodeTestResultStatus');
export const updateEditNodeTestResult = buildSelectedNodeActionObject('updateEditNodeTestResult');
export const closeSideBar = buildSelectedNodeActionObject('closeSideBar');
export const clearEditNode = buildSelectedNodeActionObject('clearEditNode');
export const setEditNodeID = buildSelectedNodeActionObject('setEditNodeID');
export const setEditNodeClick = buildSelectedNodeActionObject('setEditNodeClick');
export const updateSideBarState = buildSelectedNodeActionObject('updateSideBarState');
export const setSideBarState = buildSelectedNodeActionObject('setSideBarState');
export const setEditNode = buildSelectedNodeActionObject('setEditNode');
export const calcAndSetNeedExtraFields = buildSelectedNodeActionObject('calcAndSetNeedExtraFields');
export const calcAndGetExtraFields = buildSelectedNodeActionObject('calcAndGetExtraFields');
export const setParamInEditNode = buildSelectedNodeActionObject('setParamInEditNode');
export const actionChanged = buildSelectedNodeActionObject('actionChanged');
export const serviceChanged = buildSelectedNodeActionObject('serviceChanged');
export const getExtraFields = buildSelectedNodeActionObject('getExtraFields');
export const skipTest = buildSelectedNodeActionObject('skipTest');
export const testWidgetStep = buildSelectedNodeActionObject('testWidgetStep');
export const testStep = buildSelectedNodeActionObject('testStep');
export const triggerSetup = buildSelectedNodeActionObject('triggerSetup');
export const resetDynamicFieldsOfEditNode = buildSelectedNodeActionObject('resetDynamicFieldsOfEditNode');
export const setIsFlowTreeDragging = buildSelectedNodeActionObject('setIsFlowTreeDragging');
export const clearExtraFieldsPromise = buildSelectedNodeActionObject('clearExtraFieldsPromise');
export const setIsNodeDataLoading = buildSelectedNodeActionObject('setIsNodeDataLoading');
export const deleteMultiSelectFieldFromParam = buildSelectedNodeActionObject('deleteMultiSelectFieldFromParam');
export const updateParamsMapKey = buildSelectedNodeActionObject('updateParamsMapKey');
export const setTestingProfile = buildSelectedNodeActionObject('setTestingProfile');
export const resetWidgetFieldTestStatus = buildSelectedNodeActionObject('resetWidgetFieldTestStatus');
export const revertTempEditNodeCopy = buildSelectedNodeActionObject('revertTempEditNodeCopy');
export const updateNotification = buildSelectedNodeActionObject('updateNotification');
export const handleAutofill = buildSelectedNodeActionObject('handleAutofill');
export const rejectAutofill = buildSelectedNodeActionObject('rejectAutofill');

const EMPTYISH_PARAM_VALUES = ['', '<p></p>'];

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [toggleNodeType.action]({ dispatch }, nodeID) {
		dispatch(setLoading.fullPath, { value: true }, { root: true });
		const ret = await dispatch(nodesToggleNodeType.fullPath, { id: nodeID }, { root: true });
		dispatch(setLoading.fullPath, { value: false }, { root: true });
		const { node_type } = ret;
		const newEditNode = { ...state.editNode, ...{ node_type } };
		dispatch(setEditNode.action, { node: newEditNode });
		return ret;
	},
	[updateEditNode.action]: debounce(async ({ commit, dispatch, getters, rootGetters }, { onFailure = async () => null }) => {
		dispatch(setIsNodeDataLoading.action, true);
		try {
			const nodeIsTemp = getters[isTemp.getter];
			let nodeToUpdate;
			if (nodeIsTemp) {
				nodeToUpdate = JSON.parse(JSON.stringify(state.editNode));
			} else {
				const nodeAttributesToUpdate = cloneDeep(state.nodeAttributesToUpdate);
				nodeToUpdate = uniq(nodeAttributesToUpdate).reduce(
					(nodeToUpdate, attribute) => ({ ...nodeToUpdate, [attribute]: state.editNode[attribute] }),
					{ id: state.editNode.id }
				);
			}
			commit(RESET_ATTRIBUTES_TO_UPDATE);
			const ret = await dispatch(updateNode.fullPath, { node: nodeToUpdate, partial: !nodeIsTemp }, { root: true });
			const nodeToUpdateAfterEditNodeHandlers = nodeIsTemp ? ret.nodes.created.toSorted((nodeA, nodeB) => nodeA.id - nodeB.id)[0] : ret;
			if (nodeIsTemp) {
				await dispatch(removeTempBlock.fullPath, null, { root: true });
				await dispatch(
					addNodeToWorkflowNodes.fullPath,
					{ id: rootGetters[selectedWorkflowId.fullPath], nodeId: nodeToUpdateAfterEditNodeHandlers.id },
					{ root: true }
				);
				// override previous edit node, we have a new id
				await dispatch(setEditNode.action, { node: nodeToUpdateAfterEditNodeHandlers });
				await dispatch(getActionsOfApp.fullPath, { id: state.editNode.service, version: state.editNode.service_version }, { root: true });
			}
			const localAfterUpdateEditNodeHandlers = cloneDeep(state.afterUpdateEditNodeHandlers);
			commit(RESET_AFTER_UPDATE_EDIT_NODE_HANDLERS);
			// run all afterUpdateEditNodeHandlers registered handlers
			await Promise.all(
				uniq(localAfterUpdateEditNodeHandlers)
					.filter((handler) => !!handler)
					.map((handler) => dispatch(handler, { node: nodeToUpdateAfterEditNodeHandlers }))
			);
			await dispatch(calcAndGetExtraFields.action, { compare: true });
			await dispatch(updateSideBarState.action);
		} catch (err) {
			console.error('updateEditNode failed', err);
			if (ErrorHandlingService.extractErrorCode(err) === NODE_ERROR_CODES.FLOW_REACHED_MAXIMUM_NODES_ALLOWED) {
				alert('The feature have reached the maximum number of nodes allowed.');
			}
			await onFailure(err);
		} finally {
			commit(RESET_TEMP_EDIT_NODE_COPY);
			dispatch(setIsNodeDataLoading.action, false);
		}
	}, 600),
	[updateEditNodeTestResultOutputLabels.action]({ commit }, payload) {
		const { value } = payload;
		commit(UPDATE_EDIT_NODE, { key: 'test_result_output_labels', value });
	},
	[updateEditNodeTestResultStatus.action]({ commit }, payload) {
		const { value } = payload;
		commit(UPDATE_EDIT_NODE, { key: 'test_result_status', value });
	},
	[updateEditNodeTestResult.action]({ commit }, payload) {
		const { value } = payload;
		commit(UPDATE_EDIT_NODE, { key: 'test_result', value });
	},
	[clearEditNode.action]({ commit }) {
		commit(SET_EDIT_NODE, {});
	},
	async [closeSideBar.action]({ dispatch, rootGetters }, payload) {
		await dispatch(setSideBarState.action, { sideBarState: 'closed' });
		await dispatch(clearEditNode.action);
		if (!payload?.avoidRedirect) {
			if (!['Feature', 'Features'].includes(router.app.$route.name)) {
				const isVersionHistoryMenuOpen = rootGetters[isVersionHistoryOpen.fullPath];
				const isViewingTaskLog = rootGetters[useIsTaskLogOpen.fullPath](router.currentRoute);
				const currentRouteParams = router.currentRoute.params;
				if (isVersionHistoryMenuOpen) {
					router.push({ name: BUILDER_VERSION_HISTORY_ROUTE_NAME, params: currentRouteParams, query: { ...router.currentRoute.query } });
				} else if (isViewingTaskLog) {
					const taskLogId = currentRouteParams.taskLogId;
					if (taskLogId) {
						router.push({ name: BUILDER_TASK_LOG_INNER_PAGE, params: currentRouteParams, query: { ...router.currentRoute.query } });
					} else {
						router.push({ name: BUILDER_DEBUG_MODE_ROUTE_NAME, params: currentRouteParams, query: { ...router.currentRoute.query } });
					}
				} else {
					router.push({ name: 'Feature', query: { ...omit(router.currentRoute.query, [INTERFACE_QUERY_PARAM]) } });
				}
			}
		}
	},
	async [setEditNodeID.action]({ commit, dispatch, rootGetters }, payload) {
		const { id: nodeId } = payload;
		// dont handle if already marked as edit node
		const isTemp = nodeId.toString().includes('temp');
		if ((state.editNode && state.editNode.id && state.editNode.id === nodeId) || isTemp) return;
		let editBlock = rootGetters[getNode.fullPath](nodeId);
		if (editBlock) {
			dispatch(setEditNode.action, { node: editBlock });
		} else {
			// if we dont have node data yet, fetch it and then try to set editBlock
			try {
				await dispatch(getNodeData.fullPath, { id: nodeId }, { root: true });
				// eslint-disable-next-line no-empty
			} catch (err) {}
			editBlock = rootGetters[getNode.fullPath](nodeId);
			if (editBlock) {
				dispatch(setEditNode.action, { node: editBlock });
			} else {
				router.push({ name: 'Feature', query: { ...router.currentRoute.query } });
			}
		}
		commit(RESET_AFTER_UPDATE_EDIT_NODE_HANDLERS);
	},
	async [setEditNodeClick.action]({ commit, dispatch }, payload) {
		if (state.isFlowTreeDragging) {
			return;
		}

		await dispatch(setLeftSideDrawerState.fullPath, null, { root: true });
		await dispatch(setIsEditorAccordionMenuOpen.fullPath, false, { root: true });

		if (state.editNode) {
			const nodeReplaced = state.editNode.id !== payload.id;
			if (!nodeReplaced) {
				await dispatch(closeSideBar.action);
				await dispatch(removeTempBlock.fullPath, null, { root: true });
				return;
			}
		}
		commit(SET_NEED_EXTRA_FIELDS, false); // reset needExtraFields state before saving the new edit node
		await dispatch(setEditNode.action, { node: payload });
		if (!WorkflowNodeService.isNodeTemp(payload)) {
			Vue.nextTick(async () => {
				await dispatch(removeTempBlock.fullPath, null, { root: true });
			});
		}
	},
	async [updateSideBarState.action]({ dispatch, getters }) {
		var sideBarState = 'action';
		if (!state.editNode) {
			sideBarState = 'closed';
		}
		if (!state.editNode.service) {
			sideBarState = 'action';
		}
		if ((getters[isWidget.getter] || getters[isEndRunWidget.getter]) && state.editNode.action) {
			// WidgetService
			sideBarState = getters[isMiniApp.getter] ? 'mini-app' : 'widget';
		}
		if (getters[isBranches.getter] && state.editNode.action) {
			// BranchesService
			sideBarState = 'branch';
		}
		if (getters[isSendNotification.getter] && state.editNode.action) {
			sideBarState = 'notification';
		}
		await dispatch(setSideBarState.action, { sideBarState });
	},
	[setSideBarState.action]({ commit }, payload) {
		commit(SET_SIDE_BAR_STATE, payload.sideBarState);
	},
	async [setEditNode.action]({ commit, dispatch, getters, rootGetters }, payload) {
		await dispatch('widgetEditor/reset', {}, { root: true }); // reset old state of widgetEditor
		const removedNode = state.editNode;
		if (removedNode?.service_slug === ServiceSlugs.SubFlow) {
			dispatch(removeWorkflowPusherConnection.fullPath, { subFlowId: removedNode.params_map.sub_flow_id }, { root: true });
		}

		const { node } = payload;
		if (node.id != router.currentRoute.params.nodeID) {
			const isVersionHistoryMenuOpen = rootGetters[isVersionHistoryOpen.fullPath];
			const isViewingTaskLog = rootGetters[useIsTaskLogOpen.fullPath](router.currentRoute);
			const routePrefix = WorkflowVersionService.getRoutePrefix({ isVersionHistoryOpen: isVersionHistoryMenuOpen, isViewingTaskLog });
			router.push({
				name: `${routePrefix}${BLOCK_EDITOR_ROUTE_NAME}`,
				params: { nodeID: node.id },
				query: { ...router.currentRoute.query },
			});
		}

		commit(SET_EDIT_NODE, node);

		await dispatch(updateSideBarState.action);
		dispatch(calcAndGetExtraFields.action, { compare: true });

		switch (node.service_slug) {
			case ServiceSlugs.EndRun:
				if (getters[isEndRunWidget.getter]) {
					dispatch(
						initWidgetFields.fullPath,
						{ id: state.editNode.params_map.widget_id, workflowVersionId: state.editNode.workflow_version },
						{ root: true }
					);
				}
				break;
			case 'widgets': // WidgetService
				dispatch(
					initWidgetFields.fullPath,
					{ id: state.editNode.params_map.widget_id, workflowVersionId: state.editNode.workflow_version },
					{ root: true }
				);
				break;
			case 'filter': // Filter Helper
				// console.log('here come special care for this service to prepare the data if needed'); // todo
				break;
			case ServiceSlugs.SubFlow:
				if (node.params_map.sub_flow_id) {
					dispatch(createWorkflowPusherConnection.fullPath, { subFlowId: node.params_map.sub_flow_id }, { root: true });
				}
				break;
			default:
				break;
		}
	},
	async [calcAndSetNeedExtraFields.action]({ commit, dispatch, getters, rootGetters }) {
		let value = false;
		if (state.editNode.service && state.editNode.action) {
			await dispatch(getActionsOfApp.fullPath, { id: state.editNode.service, version: state.editNode.service_version }, { root: true });
			const action = rootGetters[getAction.fullPath](state.editNode.action);
			let service = rootGetters[getService.fullPath](state.editNode.service);
			if (!service) {
				await dispatch(getServiceData.fullPath, { id: state.editNode.service }, { root: true });
				service = rootGetters[getService.fullPath](state.editNode.service);
			}
			const credentialsProperlyFilled = WorkflowNodeService.checkIfCredentialsAreValidForNode(
				rootGetters[editedWorkflowVersion.fullPath],
				service,
				state.editNode,
				action.skip_auth,
				rootGetters[getAccountConnection.fullPath],
				rootGetters[getConfigurationFieldResponse.fullPath],
				rootGetters[isConfigurationsOn.fullPath]
			);
			const isSearchOrCreateAction = rootGetters[isActionSearchOrCreateOfService.fullPath](state.editNode.service, state.editNode.action);
			const needToFetch =
				action && action.has_dynamic_fields && (!isSearchOrCreateAction || getters[isSearchOrCreate.getter]) && credentialsProperlyFilled;
			if (needToFetch) {
				if (action?.alwaysFetchDynamicFields) {
					value = true;
				} else {
					const sourceOfEditNode = rootGetters[getNode.fullPath](state.editNode.id);
					let paramsMap = sourceOfEditNode?.params_map || {}; // we want to check if all required fields that on the node object came from server are filled

					if (sourceOfEditNode.user_schema_mappings_source_field_uuid) {
						paramsMap = WorkflowNodeService.extendParamsMapWithParamsFromDynamicMapperIfExists(
							sourceOfEditNode,
							rootGetters[getConfigurationAndInterfaceIdByFieldUUID.fullPath],
							rootGetters[getConfigurationFieldResponse.fullPath],
							rootGetters[isConfigurationsOn.fullPath],
							paramsMap
						);
					}
					const actionParams = rootGetters[getActionParams.fullPath](state.editNode.action);
					value = WorkflowNodeService.paramsAreProperlyFilledInParamsMap(actionParams, paramsMap);
				}
			}
		}
		commit(SET_NEED_EXTRA_FIELDS, value);
	},
	async [calcAndGetExtraFields.action]({ commit, dispatch }, payload) {
		const { compare: compareOld } = payload;
		const oldNeedExtraFields = state.needExtraFields;
		await dispatch(calcAndSetNeedExtraFields.action);
		// extra flag we might want to turn off and prevent getExtraFields
		const getExtra = !compareOld || state.needExtraFields !== oldNeedExtraFields || state.forceLoadDynamicFields;

		// we clear the flag that forces us to reload dynamic fields if its on
		if (state.forceLoadDynamicFields) {
			commit(SET_FORCE_LOAD_DYNAMIC_FIELDS, false);
		}

		commit(SET_LOADING_EXTRA_FIELDS, false);
		if (state.needExtraFields) {
			if (getExtra) {
				dispatch(getExtraFields.action);
			}
		} else {
			dispatch(clearExtraFieldsPromise.action);
			commit(SET_NODE_PARAMS, []);
		}
	},
	[setParamInEditNode.action]({ commit, dispatch }, payload) {
		const { key } = payload;
		if (state.tempEditNodeCopy === null) {
			commit(SYNC_TEMP_EDIT_NODE_COPY);
		}
		const options = payload.options || {};
		if (['credentials', 'credentials_type', 'source_field_uuid', 'create_new_source_field'].includes(key)) {
			commit(UPDATE_EDIT_NODE_CREDENTIALS, payload);
			commit(ADD_ATTRIBUTE_TO_UPDATE, 'credentials_configuration');
		} else {
			commit(UPDATE_EDIT_NODE, payload);
			commit(ADD_ATTRIBUTE_TO_UPDATE, key);
		}

		switch (key) {
			case 'service':
				commit(ADD_AFTER_UPDATE_EDIT_NODE_HANDLER, serviceChanged.action);
				break;
			case 'action':
				commit(UPDATE_EDIT_NODE, { key: 'params_map', value: {} });
				commit(ADD_ATTRIBUTE_TO_UPDATE, 'params_map');
				commit(UPDATE_EDIT_NODE, { key: 'params_map_labels', value: {} });
				commit(ADD_ATTRIBUTE_TO_UPDATE, 'params_map_labels');
				commit(ADD_AFTER_UPDATE_EDIT_NODE_HANDLER, actionChanged.action);
				break;
			case 'params_map': {
				commit(SET_FORCE_LOAD_DYNAMIC_FIELDS, !!options.reloadDynamic);
				break;
			}
			case 'credentials_type':
			case 'source_field_uuid':
			case 'create_new_source_field':
				commit(ADD_AFTER_UPDATE_EDIT_NODE_HANDLER, setEditNode.action);
				break;
			default:
				break;
		}
		if (!options.preventUpdateEditNode) {
			return dispatch(updateEditNode.action, { onFailure: payload.onFailure });
		}
	},
	async [actionChanged.action]({ commit, dispatch, rootGetters }, payload) {
		const { node } = payload;
		if (rootGetters[isAiAutofillEnabled.fullPath]) {
			commit(RESET_AUTOFILL);
			dispatch(handleAutofill.action);
		}
		return Promise.all([dispatch(getUserCredentials.fullPath, { forceGet: true }, { root: true }), dispatch(setEditNode.action, { node })]);
	},
	[serviceChanged.action]({ dispatch }, payload) {
		if (state.editNode?.service) {
			const { node } = payload;
			dispatch(getActionsOfApp.fullPath, { id: state.editNode.service, version: state.editNode.service_version }, { root: true });
			dispatch(setEditNode.action, { node });
		} else {
			console.error('serviceChanged called but editNode value is unexpected:', JSON.stringify(state.editNode));
		}
	},
	[clearExtraFieldsPromise.action]({ commit }) {
		if (state.getExtraFieldsPromise) {
			state.getExtraFieldsPromise.cancel();
			commit(SET_GET_EXTRA_FIELDS_PROMISE, null);
			commit(SET_LOADING_EXTRA_FIELDS, false);
		}
	},
	[getExtraFields.action]({ commit, dispatch }) {
		dispatch(clearExtraFieldsPromise.action);
		// commit(SET_NODE_PARAMS, [])
		if (!state.editNode.action) return;
		const currentEditNodeId = state.editNode.id;
		const getExtraFieldsPromise = createCancellablePromise(() => {
			commit(SET_LOADING_EXTRA_FIELDS, true);
			return api.getExtraFields(state.editNode.id);
		});
		getExtraFieldsPromise.promise.then((ret) => {
			commit(SET_GET_EXTRA_FIELDS_PROMISE, null);
			if (currentEditNodeId !== state.editNode.id) {
				return;
			}
			if (ret.error) {
				commit(SET_NODE_PARAMS_ID, currentEditNodeId);
				commit(SET_NODE_PARAMS, []);
				commit(SET_LOADING_EXTRA_FIELDS, false);
				if (ret.message) {
					alert(ret.message);
				}
				return;
			}
			commit(SET_LOADING_EXTRA_FIELDS, false);
			var filtered = ret.filter(function (obj) {
				if (!obj) return;
				const { key } = obj;
				return !this.has(key) && this.add(key);
			}, new Set()); // Filter duplicates
			commit(SET_NODE_PARAMS_ID, currentEditNodeId);
			commit(SET_NODE_PARAMS, filtered);
		});
		commit(SET_GET_EXTRA_FIELDS_PROMISE, getExtraFieldsPromise);
		return getExtraFieldsPromise.promise;
	},
	async [skipTest.action]({ dispatch }) {
		const ret = await dispatch(nodeSkipTest.fullPath, { id: state.editNode.id }, { root: true });
		await dispatch(updateEditNodeTestResultData.action, ret);
		return ret;
	},
	async [updateEditNodeTestResultData.action]({ dispatch }, payload) {
		const { test_result_status: testResultStatus, test_result: testResult, test_result_output_labels: testResultOutputLabels } = payload;
		await dispatch(updateEditNodeTestResultOutputLabels.action, { value: testResultOutputLabels });
		await dispatch(updateEditNodeTestResultStatus.action, { value: testResultStatus });
		await dispatch(updateEditNodeTestResult.action, { value: testResult });
	},
	async [testWidgetStep.action]({ dispatch }, payload) {
		const ret = await dispatch(testStep.action, { widgetData: payload });
		return ret;
	},
	async [testStep.action]({ dispatch }, payload) {
		let ret = {};
		ret = await dispatch(nodeTestStep.fullPath, { id: state.editNode.id, ...payload }, { root: true });
		if (ret && ret.hasOwnProperty('test_result')) {
			dispatch(updateEditNodeTestResultData.action, ret);
		} else if (ret?.error) {
			ret = { test_result_status: TEST_RESULT_STATUSES.ERROR, test_result: { error: true, message: 'Failed trying to test step' } };
			await dispatch(updateEditNodeTestResultData.action, ret);
		}
		return ret;
	},
	async [triggerSetup.action]({ dispatch }) {
		const ret = await api.triggerSetup(state.editNode.id);
		dispatch(reloadSelectedWorkflow.fullPath, {}, { root: true });
		return ret;
	},
	async [resetDynamicFieldsOfEditNode.action]({ dispatch }) {
		const ret = await api.resetDynamicFields(state.editNode.id, false);
		if (ret.success) {
			const { removed_params: removedParams } = ret;
			const paramsMap = { ...state.editNode.params_map };
			const paramsMapLabels = { ...state.editNode.params_map_labels };
			removedParams.forEach((removedFieldKey) => {
				delete paramsMap[removedFieldKey];
				delete paramsMapLabels[removedFieldKey];
			});
			dispatch(setParamInEditNode.action, { key: 'params_map', value: paramsMap });
			dispatch(setParamInEditNode.action, { key: 'params_map_labels', value: paramsMapLabels });
		}
		return ret;
	},
	[setIsFlowTreeDragging.action]({ commit }, payload) {
		commit(IS_FLOW_TREE_DRAGGING, payload);
	},
	[setIsNodeDataLoading.action]({ commit }, payload) {
		commit(SET_IS_NODE_DATA_LOADING, payload);
	},
	[updateParamsMapKey.action]({ dispatch }, payload) {
		const { paramKey, value, resetParamsMapLabelForKey, preventUpdateEditNode = false } = payload;
		const paramsMap = cloneDeep(state.editNode.params_map) || {};
		paramsMap[paramKey] = value;
		dispatch(setParamInEditNode.action, { key: 'params_map', value: paramsMap, options: { preventUpdateEditNode } });
		if (resetParamsMapLabelForKey) {
			dispatch(setParamInEditNode.action, {
				key: 'params_map_labels',
				value: { ...state.editNode.params_map_labels, [paramKey]: null },
				options: { preventUpdateEditNode },
			});
		}
	},
	[deleteMultiSelectFieldFromParam.action]({ dispatch }, payload) {
		const { paramKey, value } = payload;
		const paramsMap = cloneDeep(state.editNode.params_map) || {};
		const paramsMapLabels = cloneDeep(state.editNode.params_map_labels) || {};
		const filteredMultiSelectList = paramsMap[paramKey].filter(
			(multiSelectObject) => multiSelectObject.value !== value && multiSelectObject.option !== value
		);
		paramsMap[paramKey] = filteredMultiSelectList;
		paramsMapLabels[paramKey] = filteredMultiSelectList.map((multiSelectObj) => multiSelectObj.option).join(', ');
		dispatch(setParamInEditNode.action, { key: 'params_map', value: paramsMap });
		dispatch(setParamInEditNode.action, { key: 'params_map_labels', value: paramsMapLabels });
	},
	[setTestingProfile.action]({ commit }, value) {
		commit(SET_TESTING_PROFILE, value);
	},
	[resetWidgetFieldTestStatus.action]({ dispatch }, widgetFieldId) {
		return dispatch(resetNodeWidgetFieldTestStatus.fullPath, { widgetFieldId, nodeId: state.editNode.id }, { root: true });
	},
	async [revertTempEditNodeCopy.action]({ dispatch }) {
		state.tempEditNodeCopy && (await dispatch(setEditNode.action, { node: state.tempEditNodeCopy }));
	},

	async [updateNotification.action]({ commit, dispatch }, { fields }) {
		await dispatch(setIsNodeDataLoading.action, true);
		try {
			const updatedNotification = await notificationsApi.update({ nodeId: state.editNode.id, fields });
			commit(UPDATE_EDIT_NODE, { key: 'notification', value: updatedNotification });
		} catch (e) {
			console.error('updateNotification failed', JSON.stringify(e?.response?.data || e));
		} finally {
			await dispatch(setIsNodeDataLoading.action, false);
		}
	},

	[rejectAutofill.action]({ commit }, { paramKey, rejectedValue }) {
		commit(REMOVE_AUTOFILL, { paramKey, value: rejectedValue });
	},

	async [handleAutofill.action]({ dispatch, getters, rootGetters, commit }) {
		commit(SET_IS_AUTOFILL_LOADING, true);
		try {
			const workflowVersion = rootGetters[editedWorkflowVersion.fullPath];
			const service = rootGetters[getService.fullPath](state.editNode.service);
			const action = getters[editNodeAction.getter];
			const previousSteps = getters[previousStepsDataByNodeLocation.getter];

			const neededGetters = [getNodeByUuidForWorkflowVersion, getService, getAction, getWidget].reduce((result, getter) => {
				result[getter.getter] = rootGetters[getter.fullPath];
				return result;
			}, {});

			const actionParams = rootGetters[getActionParams.fullPath](state.editNode.action);
			const paramsToAutofill = actionParams.filter(shouldAutofillParam);

			const canUpdateParam = (paramKey) =>
				!(paramKey in state.editNode.params_map) || EMPTYISH_PARAM_VALUES.includes(state.editNode.params_map[paramKey]);

			const handleParam = async (param) => {
				if (!canUpdateParam(param.key)) {
					console.error(`aborting autofill request of "${param.key}" because it's not empty`);
					return;
				}

				const suggestedKey = await suggestAutofill({
					workflowVersion,
					service,
					action,
					param,
					previousSteps,
					getters: neededGetters,
				});

				if (!canUpdateParam(param.key)) {
					console.error(`aborting autofill applying of "${param.key}" because it's not empty`);
					return;
				}

				commit(ADD_AUTOFILL, { paramKey: param.key, value: suggestedKey });

				return dispatch(updateParamsMapKey.action, {
					paramKey: param.key,
					value: suggestedKey,
					resetParamsMapLabelForKey: true,
					preventUpdateEditNode: false,
				});
			};

			const tasks = paramsToAutofill.map(handleParam);

			return await Promise.all(tasks); // awaiting so exceptions are thrown in current scope
		} catch (e) {
			commit(SET_AUTOFILL_ERROR, e);
			throw e;
		} finally {
			commit(SET_IS_AUTOFILL_LOADING, false);
		}
	},
};

export default actions;
